import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import {
  CogIcon,
  TruckIcon,
  ClockIcon,
  UsersIcon,
  BellIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import {
  getTransportSettings,
  updateTransportSettings,
  type TransportSettings
} from '@/api/tripsApi'
import toast from 'react-hot-toast'

// Form validation schema
const transportSettingsSchema = z.object({
  enabled: z.boolean(),
  requireStudentSelection: z.boolean(),
  autoCreateSessions: z.boolean(),
  defaultPickupTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Must be in HH:MM format'),
  defaultDropoffTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Must be in HH:MM format'),
  allowMultipleTripsPerDay: z.boolean(),
  trackingEnabled: z.boolean(),
  parentNotifications: z.boolean()
})

type TransportSettingsForm = z.infer<typeof transportSettingsSchema>

interface TransportSettingsProps {
  institutionId?: string
}

const TransportSettings: React.FC<TransportSettingsProps> = ({ institutionId }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<TransportSettings | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useForm<TransportSettingsForm>({
    resolver: zodResolver(transportSettingsSchema),
    defaultValues: {
      enabled: false,
      requireStudentSelection: true,
      autoCreateSessions: true,
      defaultPickupTime: '07:00',
      defaultDropoffTime: '16:00',
      allowMultipleTripsPerDay: true,
      trackingEnabled: true,
      parentNotifications: true
    }
  })

  const transportEnabled = watch('enabled')
  const parentNotifications = watch('parentNotifications')
  const trackingEnabled = watch('trackingEnabled')

  // Load current settings
  useEffect(() => {
    loadSettings()
  }, [institutionId])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await getTransportSettings(institutionId)
      const transportData = response.data.transport
      setSettings(transportData)
      reset(transportData)
    } catch (error) {
      console.error('Failed to load transport settings:', error)
      toast.error('Failed to load transport settings')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: TransportSettingsForm) => {
    try {
      setSaving(true)
      await updateTransportSettings(data, institutionId)
      setSettings(data as TransportSettings)
      toast.success('Transport settings updated successfully')
    } catch (error: any) {
      console.error('Failed to update transport settings:', error)
      toast.error(error.response?.data?.message || 'Failed to update transport settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200"
    >
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <TruckIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Transport Configuration</h2>
            <p className="text-sm text-gray-600">Configure transport settings for your school</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Master Enable Switch */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full ${transportEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
              <div>
                <h3 className="font-semibold text-gray-900">Enable Transport Module</h3>
                <p className="text-sm text-gray-600">Allow students to be assigned to transport routes</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {transportEnabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-6"
          >
            {/* Student Selection Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <UsersIcon className="w-5 h-5 text-blue-600" />
                  <span>Student Management</span>
                </h4>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('requireStudentSelection')}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Require Student Selection</p>
                    <p className="text-xs text-gray-500">Must select students when creating trips</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('allowMultipleTripsPerDay')}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Multiple Trips Per Day</p>
                    <p className="text-xs text-gray-500">Allow students in multiple daily trips</p>
                  </div>
                </label>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <CogIcon className="w-5 h-5 text-blue-600" />
                  <span>Automation Settings</span>
                </h4>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('autoCreateSessions')}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Auto-Create Sessions</p>
                    <p className="text-xs text-gray-500">Automatically generate daily trip sessions</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('trackingEnabled')}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">GPS Tracking</p>
                    <p className="text-xs text-gray-500">Enable real-time vehicle tracking</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('parentNotifications')}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Parent Notifications</p>
                    <p className="text-xs text-gray-500">Send pickup/dropoff alerts to parents</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Default Times */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 flex items-center space-x-2 mb-4">
                  <ClockIcon className="w-5 h-5 text-blue-600" />
                  <span>Default Schedule</span>
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Pickup Time
                    </label>
                    <Input
                      type="time"
                      {...register('defaultPickupTime')}
                      className="block w-full"
                    />
                    {errors.defaultPickupTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.defaultPickupTime.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Dropoff Time
                    </label>
                    <Input
                      type="time"
                      {...register('defaultDropoffTime')}
                      className="block w-full"
                    />
                    {errors.defaultDropoffTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.defaultDropoffTime.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 flex items-center space-x-2 mb-4">
                  <BellIcon className="w-5 h-5 text-blue-600" />
                  <span>Notification Settings</span>
                </h4>
                
                <div className="space-y-3">
                  {parentNotifications && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        <p className="text-sm text-green-800">
                          Parents will receive pickup and dropoff notifications
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {trackingEnabled && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <MapPinIcon className="w-5 h-5 text-blue-600" />
                        <p className="text-sm text-blue-800">
                          Real-time GPS tracking enabled for all trips
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Information Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <TruckIcon className="w-5 h-5 text-blue-600" />
                  <h5 className="font-medium text-blue-900">Trip Sessions</h5>
                </div>
                <p className="text-sm text-blue-800">
                  Daily trip sessions are auto-generated like academic sessions
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <UsersIcon className="w-5 h-5 text-green-600" />
                  <h5 className="font-medium text-green-900">Student Enrollment</h5>
                </div>
                <p className="text-sm text-green-800">
                  Students can be enrolled during user creation or trip management
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="w-5 h-5 text-purple-600" />
                  <h5 className="font-medium text-purple-900">Session Management</h5>
                </div>
                <p className="text-sm text-purple-800">
                  Drivers can start and end trip sessions like teaching sessions
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={loadSettings}
            className="flex items-center space-x-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span>Reset</span>
          </Button>

          <Button
            type="submit"
            disabled={!isDirty || saving}
            className="flex items-center space-x-2"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4" />
                <span>Save Settings</span>
              </>
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  )
}

export default TransportSettings